import { BoardView, Button, EmptyState, WorkOrderIcon } from '@olytic/foundation';
import { useMemo } from 'react';

import { TypeMark, PriorityMark } from './marks';
import {
  assigneeValue, type Filters, fieldValues, type GroupKey, groupsFor, isBlocked, type Lookups, matches, shortDate, STATUSES, dueState, todayIso,
} from './model';
import { PERSON_DRAG, type AvatarOf, type Patch, type SwimlaneMove } from './WorkPage';
import type { WorkItem, WorkStatus } from './workApi';

/** Status lanes; drag between and within them, and between swimlanes when grouped. */
export function WorkBoard({
  items, filters, group, order, lookups, avatarOf, openId, showSubtasks, canQuickAdd, closedLanes, onLaneToggle,
  onOpen, onPatch, onCreate, onAssign, onSwimlaneMove, onArchiveDone,
}: {
  items: WorkItem[];
  filters: Filters;
  group: GroupKey;
  order: (a: WorkItem, b: WorkItem) => number;
  lookups: Lookups;
  avatarOf: AvatarOf;
  openId: string | null;
  showSubtasks: boolean;
  canQuickAdd: boolean;
  closedLanes: string[];
  onLaneToggle: (key: string) => void;
  onOpen: (id: string) => void;
  onPatch: Patch;
  onCreate: (status: WorkStatus, title: string) => void;
  onAssign: (item: WorkItem, personId: string) => void;
  onSwimlaneMove: SwimlaneMove;
  onArchiveDone: (rows: WorkItem[]) => void;
}): JSX.Element {
  const today = todayIso();
  const rows = useMemo(
    () => items
      .filter((i) => !i.archived_at && i.type !== 'epic' && (showSubtasks || i.type !== 'subtask') && matches(i, filters, { list: false }))
      .sort(order),
    [items, filters, showSubtasks, order],
  );
  const children = useMemo(() => {
    const out = new Map<string, WorkItem[]>();
    for (const i of items) {
      if (!i.parent_id) continue;
      if (!out.has(i.parent_id)) out.set(i.parent_id, []);
      out.get(i.parent_id)!.push(i);
    }
    return out;
  }, [items]);
  const groups = group === 'none' ? null : groupsFor(rows, group, lookups);

  if (items.length > 0 && rows.length === 0 && !groups?.length && (filters.q || Object.values(filters.facets).some((v) => v.length))) {
    return <EmptyState icon={<WorkOrderIcon />} title="Nothing matches" description="No work items match these filters." />;
  }

  return (
    <BoardView<WorkItem>
      columns={STATUSES.map((s) => ({ key: s.key, label: s.label }))}
      rows={rows}
      getRowId={(r) => r.id}
      groupBy={(r) => r.status}
      moveMenu={false}
      selectedId={openId}
      onCardClick={(r) => onOpen(r.id)}
      {...(groups ? {
        swimlanes: groups.map((g) => ({ key: g.key, label: g.label })),
        swimlaneOf: (r: WorkItem) => fieldValues(r, group === 'none' ? 'status' : group)[0] ?? '',
        swimlaneCount: (n: number) => `${n} work item${n === 1 ? '' : 's'}`,
      } : {})}
      onMove={(row, toColumn, at) => {
        const patch = { status: toColumn as WorkStatus, position: { before_id: at.beforeId, after_id: at.afterId } };
        let extra = {};
        if (at.swimlane !== undefined && group !== 'none') {
          const from = fieldValues(row, group)[0] ?? '';
          const move = onSwimlaneMove(row, from, at.swimlane);
          if (move === false) return;
          if (move) extra = move;
        }
        const moved = row.status !== toColumn;
        void onPatch(row, { ...patch, ...extra }, moved ? `${row.key} moved to ${STATUSES.find((s) => s.key === toColumn)?.label}` : undefined);
      }}
      {...(canQuickAdd ? { onQuickAdd: (column: string, title: string) => onCreate(column as WorkStatus, title), quickAddPlacement: 'header' as const } : {})}
      collapsibleColumns
      collapsedColumns={closedLanes}
      onColumnToggle={onLaneToggle}
      className="px-0"
      laneAction={(column, laneRows) => (column === 'done' && laneRows.length > 0 ? (
        <Button variant="link" size="sm" onClick={() => onArchiveDone(laneRows)}>Archive all</Button>
      ) : null)}
      cardDrop={{
        accepts: (types) => types.includes(PERSON_DRAG),
        onDrop: (row, data) => {
          const personId = data.getData(PERSON_DRAG);
          if (personId) onAssign(row, personId);
        },
      }}
      card={{
        eyebrow: (r) => r.epic_title,
        title: (r) => r.title,
        labels: (r) => r.labels,
        meta: (r) => {
          const kids = children.get(r.id) ?? [];
          return kids.length ? `${kids.filter((k) => k.status === 'done').length} of ${kids.length} subtasks done` : null;
        },
        icon: (r) => <TypeMark type={r.type} />,
        key: (r) => r.key,
        blocked: (r) => (isBlocked(r) ? 'Blocked' : null),
        due: (r) => {
          const state = dueState(r, today);
          if (!r.due_date || r.status === 'done') return null;
          return (
            <span className={state === 'overdue' ? 'text-[var(--danger-fg)]' : state === 'today' ? 'text-[var(--warning-fg)]' : 'text-[var(--fg-muted)]'}>
              {state === 'today' ? 'Today' : shortDate(r.due_date)}
            </span>
          );
        },
        priority: (r) => <PriorityMark priority={r.priority} />,
        avatar: (r) => avatarOf(assigneeValue(r)),
      }}
    />
  );
}
